import React, { useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Div, Text } from "atomize"
import { AnimatePresence, motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo, faXmark } from "@fortawesome/free-solid-svg-icons"
import Paragraph from "../atoms/Paragraph"
import { theme } from "../settings/config"
import { getPtrLevel, PTR_LEVELS } from "../utils/func.utils"

// quanto sfumano i bordi della riga, dal lato dove c'è ancora da scorrere
const FADE = "2rem"

// La sfumatura si fa con una maschera e non con un velo del colore della
// scheda: così vale in tutti e due i temi senza saperne niente.
const fadeMask = (left, right) =>
	`linear-gradient(to right, ${left ? "transparent" : "black"} 0, black ${left ? FADE : "0"}, black calc(100% - ${right ? FADE : "0px"}), ${right ? "transparent" : "black"} 100%)`

// La guida: tutte le voci della scala con la loro descrizione. Toccarne una
// la segna, e il bottone in fondo la usa: si possono rileggere un paio di
// voci prima di decidere, e chiudendo senza il bottone non cambia niente.
// Sta sopra al modulo della lezione, che è già un
// foglio: per questo va in fondo alla pagina e non dentro al modulo, che si
// trascina e porterebbe con sé anche lei.
function PtrLevelGuide({ value, onPick, onClose }) {
	const [marked, setMarked] = useState(value)
	const markedLevel = getPtrLevel(marked)

	return createPortal(
		<>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 0.4 }}
				exit={{ opacity: 0 }}
				onClick={onClose}
				style={{
					position: "fixed",
					inset: 0,
					background: theme.colors.overlay,
					zIndex: 1100,
				}}
			/>
			<motion.div
				role="dialog"
				aria-modal="true"
				aria-label="Livelli PTR"
				initial={{ y: "100%" }}
				animate={{ y: 0 }}
				exit={{ y: "100%" }}
				transition={{ type: "tween", ease: [0.04, 0.62, 0.23, 0.98], duration: 0.35 }}
				style={{
					position: "fixed",
					left: 0,
					right: 0,
					bottom: 0,
					maxHeight: "90vh",
					display: "flex",
					flexDirection: "column",
					zIndex: 1101,
				}}>
				<Div
					bg="background"
					rounded="t-20px"
					d="flex"
					flexDir="column"
					maxW="32rem"
					w="100%"
					m={{ x: "auto" }}
					p={{ t: "1rem", x: "1rem", b: "calc(1rem + env(safe-area-inset-bottom))" }}
					style={{ minHeight: 0 }}>
					<Div d="flex" justify="space-between" align="center" p={{ l: "0.25rem" }}>
						<Text textSize="title" textWeight="bold" textColor="gunmetal">
							Livelli PTR
						</Text>
						<Div
							tag="button"
							type="button"
							aria-label="Chiudi"
							w="2.75rem"
							h="2.75rem"
							d="flex"
							align="center"
							justify="center"
							bg="transparent"
							border="0"
							cursor="pointer"
							onClick={onClose}>
							<FontAwesomeIcon
								icon={faXmark}
								size="lg"
								color={theme.colors.gunmetal}
							/>
						</Div>
					</Div>
					<Text
						textSize="caption"
						textColor="text_light"
						p={{ l: "0.25rem" }}
						m={{ b: "0.75rem" }}>
						Tocca un livello per sceglierlo.
					</Text>
					<Div d="flex" flexDir="column" style={{ gap: "0.4rem", overflowY: "auto" }}>
						{PTR_LEVELS.map(level => {
							const isSelected = marked === level.value

							return (
								<Div
									key={level.value}
									tag="button"
									type="button"
									aria-pressed={isSelected}
									d="flex"
									align="flex-start"
									w="100%"
									p="0.6rem 0.75rem"
									rounded="16px"
									border="2px solid"
									borderColor={
										isSelected ? theme.colors.gunmetal : theme.colors.surface
									}
									bg={theme.colors.surface}
									cursor="pointer"
									textAlign="left"
									style={{ gap: "0.75rem", font: "inherit", flexShrink: 0 }}
									onClick={() => setMarked(level.value)}>
									<Div
										w="2.75rem"
										h="1.75rem"
										rounded="14px"
										d="flex"
										align="center"
										justify="center"
										bg={
											isSelected
												? theme.colors.gunmetal_fill
												: theme.colors.gunmetal_lighter
										}
										textColor={
											isSelected ? theme.colors.white : theme.colors.gunmetal
										}
										textWeight="bold"
										textSize="caption"
										style={{ flexShrink: 0 }}>
										{level.label}
									</Div>
									<Text textSize="caption" textColor="text">
										{level.description}
									</Text>
								</Div>
							)
						})}
					</Div>
					<Div
						tag="button"
						type="button"
						disabled={!markedLevel}
						w="100%"
						h="3.5rem"
						m={{ t: "1rem" }}
						rounded="20px"
						border="0"
						bg={markedLevel ? theme.colors.primary : theme.colors.gray_light}
						textColor={theme.colors.white}
						textWeight="bold"
						textSize="subheader"
						cursor={markedLevel ? "pointer" : "default"}
						style={{ font: "inherit", flexShrink: 0 }}
						onClick={() => onPick(marked)}>
						{markedLevel ? `Usa il ${markedLevel.label}` : "Scegli un livello"}
					</Div>
				</Div>
			</motion.div>
		</>,
		document.body,
	)
}

// Il livello PTR del CAD: facoltativo, e toccare quello già scelto lo toglie,
// come per il SAT. Nove voci non stanno su una riga: la riga scorre di lato,
// sfumata dalla parte dove ce ne sono altre. Sotto, cosa vuol dire quella
// scelta; e la guida, per leggerle tutte prima di scegliere. Lo usa anche la
// scheda tecnica dell'allievo, dove la domanda è la stessa e cambia il titolo.
function PtrLevelPicker({ value, onChange, title = "Livello PTR (facoltativo):" }) {
	const [isGuideOpen, setGuideOpen] = useState(false)
	const [fade, setFade] = useState({ left: false, right: false })
	const rowRef = useRef(null)
	const pillRefs = useRef({})
	const hasRendered = useRef(false)

	const selected = getPtrLevel(value)

	const updateFade = () => {
		const row = rowRef.current
		if (!row) return
		const left = row.scrollLeft > 1
		const right = row.scrollLeft + row.clientWidth < row.scrollWidth - 1
		setFade(previous =>
			previous.left === left && previous.right === right ? previous : { left, right },
		)
	}

	// La scelta si vede sempre: aprendo una lezione col 4.5 la riga ci arriva
	// da sola, e lo stesso se lo si sceglie dalla guida. Se è già in vista non
	// si sposta niente, che la riga non scappi da sotto il dito.
	useLayoutEffect(() => {
		const row = rowRef.current
		const pill = pillRefs.current[value]
		if (row && pill) {
			const isVisible =
				pill.offsetLeft >= row.scrollLeft &&
				pill.offsetLeft + pill.offsetWidth <= row.scrollLeft + row.clientWidth
			if (!isVisible) {
				const target = pill.offsetLeft - (row.clientWidth - pill.offsetWidth) / 2
				if (hasRendered.current && row.scrollTo)
					row.scrollTo({ left: target, behavior: "smooth" })
				else row.scrollLeft = target
			}
		}
		hasRendered.current = true
		updateFade()
	}, [value])

	const pick = levelValue => {
		onChange(levelValue)
		setGuideOpen(false)
	}

	return (
		<>
			<Paragraph
				title={title}
				secondaryAction={
					<Div
						tag="button"
						type="button"
						d="flex"
						align="center"
						p="0.25rem 0"
						bg="transparent"
						border="0"
						cursor="pointer"
						textColor="primary"
						textWeight="bold"
						style={{ gap: "0.35rem", font: "inherit" }}
						onClick={() => setGuideOpen(true)}>
						<FontAwesomeIcon icon={faCircleInfo} />
						Guida
					</Div>
				}
			/>
			<div
				ref={rowRef}
				onScroll={updateFade}
				style={{
					position: "relative",
					display: "flex",
					marginTop: "0.5rem",
					gap: "0.4rem",
					overflowX: "auto",
					scrollbarWidth: "none",
					maskImage: fadeMask(fade.left, fade.right),
					WebkitMaskImage: fadeMask(fade.left, fade.right),
				}}>
				{PTR_LEVELS.map(level => {
					const isSelected = value === level.value

					return (
						<motion.div
							key={level.value}
							ref={element => (pillRefs.current[level.value] = element)}
							whileTap={{ scale: 0.9 }}
							style={{ flexShrink: 0 }}>
							<Div
								tag="button"
								type="button"
								aria-pressed={isSelected}
								aria-label={`Livello PTR ${level.label}`}
								d="flex"
								align="center"
								justify="center"
								minW="3.5rem"
								h="2.25rem"
								p="0 0.75rem"
								rounded="15px"
								border="2px solid"
								borderColor={
									isSelected ? theme.colors.gunmetal : theme.colors.surface
								}
								bg={theme.colors.background}
								textColor="text"
								textWeight={isSelected ? "bold" : "normal"}
								cursor="pointer"
								transition
								style={{ font: "inherit" }}
								onClick={() => onChange(isSelected ? null : level.value)}>
								{level.label}
							</Div>
						</motion.div>
					)
				})}
			</div>
			{selected && (
				<Div
					m={{ t: "0.6rem" }}
					p="0.6rem 0.75rem"
					rounded="12px"
					bg={theme.colors.background}
					textSize="caption"
					textColor="gunmetal">
					<Text tag="span" textWeight="bold" textSize="caption" textColor="gunmetal">
						{selected.label}
					</Text>
					{" · "}
					{selected.description}
				</Div>
			)}
			<AnimatePresence>
				{isGuideOpen && (
					<PtrLevelGuide
						value={value}
						onPick={pick}
						onClose={() => setGuideOpen(false)}
					/>
				)}
			</AnimatePresence>
		</>
	)
}

export default PtrLevelPicker
